import type { components } from '@trackercop/contracts';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { formatUsd } from '@/lib/format';

type CostLine = components['schemas']['CostLine'];

interface CostLineTableProps {
  lines: CostLine[];
}

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 });

function formatUsage(value: number, unit: CostLine['unit']): string {
  if (unit === 'flat_month') return '—';
  if (unit === 'gb_month') {
    const gb = value / (1024 * 1024 * 1024);
    return `${gb.toFixed(2)} GB-mo`;
  }
  if (unit === 'gb_second') {
    return `${compact.format(value)} GB-s`;
  }
  if (unit === 'cpu_ms' || unit === 'million_cpu_ms') {
    return `${compact.format(value)} CPU-ms`;
  }
  return compact.format(value);
}

export function CostLineTable({ lines }: CostLineTableProps) {
  const sorted = [...lines].sort((a, b) => b.costUsd - a.costUsd);
  if (sorted.length === 0) {
    return (
      <div
        className="rounded-md border bg-background p-6 text-sm text-muted-foreground"
        data-testid="cost-line-table-empty"
      >
        No cost lines yet — usage data may still be flowing in.
      </div>
    );
  }
  return (
    <Table data-testid="cost-line-table">
      <THead>
        <TR>
          <TH>Component</TH>
          <TH>Dimension</TH>
          <TH className="text-right">Usage</TH>
          <TH className="text-right">Included</TH>
          <TH className="text-right">Billable</TH>
          <TH className="text-right">Rate</TH>
          <TH className="text-right">Cost</TH>
        </TR>
      </THead>
      <TBody>
        {sorted.map((l) => (
          <TR key={`${l.component}:${l.dimension}`} data-testid={`cost-row-${l.component}-${l.dimension}`}>
            <TD className="font-mono text-xs">{l.component}</TD>
            <TD className="font-mono text-xs">{l.dimension}</TD>
            <TD className="text-right font-mono text-xs tabular-nums">
              {formatUsage(l.usage, l.unit)}
            </TD>
            <TD className="text-right font-mono text-xs tabular-nums text-muted-foreground">
              {formatUsage(l.includedUsage, l.unit)}
            </TD>
            <TD className="text-right font-mono text-xs tabular-nums">
              {formatUsage(l.billableUsage, l.unit)}
            </TD>
            <TD className="text-right font-mono text-xs tabular-nums text-muted-foreground">
              {formatUsd(l.ratePerUnitUsd)}/{l.unit.replace('_', ' ')}
            </TD>
            <TD className="text-right font-mono text-sm tabular-nums">
              {formatUsd(l.costUsd)}
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
